import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Textarea } from '~/ui/textarea'
import { Switch } from '~/ui/switch'
import { Label } from '~/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/ui/dialog'
import { Sparkles } from 'lucide-react'
import { gql } from '~/__generated__'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { toast } from '~/hooks/use-toast'

const GENERATE_AI_TRACKS_MUTATION = gql(`
  mutation GenerateAiTracksForPlaylist($input: GenerateAiTracksForPlaylistInput!) {
    generateAiTracksForPlaylist(input: $input) {
      playlist {
        id
        aiGenerationStatus {
          status
          progress
          tracksGenerated
          totalTracks
          message
          error
        }
      }
      errors
    }
  }
`)

const formSchema = z.object({
  prompt: z
    .string()
    .min(1, 'Prompt is required')
    .max(2000, 'Prompt must be 2000 characters or less'),
  negativePrompt: z.string().max(2000, 'Negative prompt must be 2000 characters or less').optional(),
  trackCount: z.coerce.number().int().min(1, 'At least 1 track').max(20, 'Maximum 20 tracks'),
  trackDuration: z.coerce
    .number()
    .int()
    .min(30, 'Minimum 30 seconds')
    .max(600, 'Maximum 600 seconds'),
  instrumental: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

interface GenerateAiTracksDialogProps {
  playlistId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onGenerated: () => void
}

export const GenerateAiTracksDialog: React.FC<GenerateAiTracksDialogProps> = ({
  playlistId,
  open,
  onOpenChange,
  onGenerated,
}) => {
  const [generateAiTracks, { loading }] = useSafeMutation(GENERATE_AI_TRACKS_MUTATION)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      prompt: '',
      negativePrompt: '',
      trackCount: 5,
      trackDuration: 180,
      instrumental: true,
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = form

  const instrumental = watch('instrumental')

  const onSubmit = async (values: FormValues) => {
    try {
      const result = await generateAiTracks({
        variables: {
          input: {
            playlistId,
            prompt: values.prompt,
            negativePrompt: values.negativePrompt || undefined,
            trackCount: values.trackCount,
            trackDurationSeconds: values.trackDuration,
            instrumental: values.instrumental,
          },
        },
      })

      const mutationErrors = result.data?.generateAiTracksForPlaylist?.errors
      if (mutationErrors && mutationErrors.length > 0) {
        toast({
          title: 'Error',
          description: mutationErrors.join(', '),
          variant: 'destructive',
        })
        return
      }

      onGenerated()
      reset()
      onOpenChange(false)
    } catch (error) {
      console.error('Error generating AI tracks:', error)
      toast({
        title: 'Error',
        description: 'Failed to generate AI tracks',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate AI Tracks
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prompt">Prompt</Label>
            <Textarea
              id="prompt"
              {...register('prompt')}
              placeholder="Describe the music you want to generate..."
              className="min-h-[100px]"
              maxLength={2000}
              disabled={loading}
            />
            {errors.prompt && <p className="text-sm text-red-600">{errors.prompt.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="negativePrompt">Negative Prompt (optional)</Label>
            <Textarea
              id="negativePrompt"
              {...register('negativePrompt')}
              placeholder="Elements to avoid..."
              className="min-h-[60px]"
              maxLength={2000}
              disabled={loading}
            />
            {errors.negativePrompt && (
              <p className="text-sm text-red-600">{errors.negativePrompt.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trackCount">Number of Tracks</Label>
              <Input
                id="trackCount"
                type="number"
                {...register('trackCount')}
                min={1}
                max={20}
                disabled={loading}
              />
              {errors.trackCount && (
                <p className="text-sm text-red-600">{errors.trackCount.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="trackDuration">Duration (seconds)</Label>
              <Input
                id="trackDuration"
                type="number"
                {...register('trackDuration')}
                min={30}
                max={600}
                disabled={loading}
              />
              {errors.trackDuration && (
                <p className="text-sm text-red-600">{errors.trackDuration.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="instrumental"
              checked={instrumental}
              onCheckedChange={(checked) => setValue('instrumental', checked)}
              disabled={loading}
            />
            <Label htmlFor="instrumental">Instrumental</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
